import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Search, Printer, Send } from "lucide-react";
import { API, axios, formatIcelandicNumber } from "@/utils/constants";
import { generatePaymentSchedule } from "@/utils/paymentSchedule";
import { calculateLoanAhk } from "@/utils/ahk";
import { MiniSpreadsheet } from "@/components/MiniSpreadsheet";

const fmtNum = (v) => {
  const digits = String(v).replace(/\D/g, "");
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};
const stripDots = (v) => String(v).replace(/\./g, "");

const TYPE_CONFIG = {
  skuldabref: {
    title: "Nýtt Skuldabréf",
    loanType: "skuldabref",
    loanPurpose: "Skuldabréf",
    hasVehicleLookup: false,
    hasPurchasePrice: false,
    accentColor: "blue",
    successMsg: "Skuldabréf skráð",
    skuldbreytingMsg: "Skuldabréf skráð. Smelltu á 'Virkja' til að ljúka skuldbreytingu.",
    editMsg: "Skuldabréf uppfært",
    offerTitle: "TILBOÐ - SKULDABRÉF",
    emailLabel: "Skuldabréf",
    showPdfAfterSave: false,
    pdfType: "skuldabref",
    validateVehicle: false,
  },
  vedskuldabref: {
    title: "Nýtt Veðskuldabréf",
    loanType: "vedskuldabref",
    loanPurpose: "Veðskuldabréf",
    hasVehicleLookup: true,
    hasPurchasePrice: true,
    accentColor: "green",
    successMsg: "Veðskuldabréf skráð",
    skuldbreytingMsg: "Veðskuldabréf skráð. Smelltu á 'Virkja' til að ljúka skuldbreytingu.",
    editMsg: "Veðskuldabréf uppfært",
    offerTitle: "TILBOÐ - VEÐSKULDABRÉF",
    emailLabel: "Veðskuldabréf",
    showPdfAfterSave: true,
    pdfType: "vedskuldabref",
    validateVehicle: true,
  },
  kaupleigusamning: {
    title: "Nýr Kaupleigusamningur",
    loanType: "kaupleigusamning",
    loanPurpose: "Kaupleigusamningur",
    hasVehicleLookup: true,
    hasPurchasePrice: true,
    accentColor: "green",
    successMsg: "Kaupleigusamningur skráður",
    skuldbreytingMsg: "Kaupleigusamningur skráður. Smelltu á 'Virkja' til að ljúka skuldbreytingu.",
    editMsg: "Kaupleigusamningur uppfærður",
    offerTitle: "TILBOÐ - KAUPLEIGUSAMNINGUR",
    emailLabel: "Kaupleigusamningur",
    showPdfAfterSave: true,
    pdfType: "kaupleigusamningur",
    validateVehicle: true,
  },
};

export const LoanFormDialog = ({
  loanType,
  formData, setFormData,
  settings, lookupLoading, lookupPerson,
  vehicleLookupLoading, setVehicleLookupLoading,
  vehicleLookupFailed, setVehicleLookupFailed,
  showMiniSpreadsheet, setShowMiniSpreadsheet,
  editMode, setEditMode, editingAppId, setEditingAppId,
  skuldbreytingOldLoanId, setSkuldbreytingOldLoanId,
  skuldbreytingOldLoanIds, loanNumberMap, fmtLoanNum,
  setEmailPreviewData, setShowEmailPreview,
  fetchApplications, saveKennitalCache, getDefaultFirstPaymentDate,
  open, onOpenChange,
  setPdfApp, setPdfType,
}) => {
  const cfg = TYPE_CONFIG[loanType] || TYPE_CONFIG.skuldabref;
  const d = formData;
  const setD = setFormData;
  const accent = cfg.accentColor;
  const accentBg = accent === "green" ? "bg-green-50 border-green-200" : "bg-blue-50 border-blue-200";
  const accentText = accent === "green" ? "text-green-600" : "text-blue-600";
  const btnClass = accent === "green" ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700";

  const doVehicleLookup = async (fastanumer) => {
    if (!fastanumer || fastanumer.length < 3) return;
    setVehicleLookupLoading(true);
    setVehicleLookupFailed(false);
    try {
      const res = await axios.post(`${API}/vehicle-lookup`, { fastanumer });
      if (res.data) {
        const toISO = (val) => {
          if (!val) return "";
          if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
          const parts = val.split(/[./]/);
          if (parts.length === 3) return `${parts[2]}-${parts[1].padStart(2,"0")}-${parts[0].padStart(2,"0")}`;
          return val;
        };
        // Build Tegund + Undirtegund
        const tegund = res.data.tegund || "";
        const undirtegund = res.data.undirtegund || "";
        const fullType = undirtegund ? `${tegund} ${undirtegund}` : tegund;
        setD(prev => ({
          ...prev,
          vehicleType: fullType || prev.vehicleType || "",
          firstRegistrationDate: toISO(res.data.fyrsta_skraning) || prev.firstRegistrationDate || "",
        }));
        toast.success("Ökutækjaupplýsingar fundust!");
      }
    } catch {
      setVehicleLookupFailed(true);
      toast.info("Uppfletting mistókst — fylltu inn handvirkt", { duration: 4000 });
    } finally {
      setVehicleLookupLoading(false);
    }
  };

  // Calculate AHK values
  const getEffectiveAmount = () => {
    if (cfg.hasPurchasePrice) {
      return parseFloat(d.amount || ((parseFloat(d.purchasePrice) || 0) - (parseFloat(d.deposit) || 0))) || 0;
    }
    return parseFloat(d.amount) || 0;
  };
  const amt = getEffectiveAmount();
  const lant = parseFloat(d.lantokugjald) || 0;
  const months = parseInt(d.months) || 0;
  const rate = parseFloat(d.vextir) || 0;
  const grs = parseFloat(d.greidslus) || 250;
  const maxAhk = 0.35 + (settings.styrivextir || 7.25) / 100;
  const { ahk } = amt > 0 && months > 0 && rate > 0 ? calculateLoanAhk(amt, lant, months, rate, grs) : { ahk: 0 };

  const calcMonthly = () => {
    const total = amt + lant;
    const mr = rate / 100 / 12;
    if (mr <= 0 || months <= 0) return 0;
    return total * (mr * Math.pow(1 + mr, months)) / (Math.pow(1 + mr, months) - 1);
  };

  const getDefaultForm = () => ({
    kennitala: "", name: "", address: "", postalCode: "", city: "", phone: "", email: "",
    amount: "", lantokugjald: "", greidslus: "250", months: "",
    vextir: settings?.vextir?.toString() || "", firstPaymentDate: getDefaultFirstPaymentDate(),
    vehicleType: "", registrationNumber: "", firstRegistrationDate: "",
    ...(cfg.hasPurchasePrice ? { purchasePrice: "", deposit: "" } : {}),
    radstofun: false, radstofunKennitala: "", radstofunName: "",
    radstofunBanki: "", radstofunHofudbokl: "", radstofunReikningur: "",
  });

  const resetAndClose = () => {
    onOpenChange(false);
    setEditMode(false);
    setEditingAppId(null);
    setD(getDefaultForm());
  };

  const handlePrint = () => {
    if (amt <= 0 || months <= 0) { toast.error("Vinsamlegast fylltu út lánsupplýsingar"); return; }
    const mp = calcMonthly();
    const total = amt + lant;
    const vehicleInfo = cfg.hasVehicleLookup
      ? `\nÖKUTÆKI:\n- Tegund: ${d.vehicleType || '-'}\n- Fastanúmer: ${d.registrationNumber || '-'}\n`
      : "";
    const content = `${cfg.offerTitle}\n========================\n\nLántaki: ${d.name || '-'}\nKennitala: ${d.kennitala || '-'}\n${vehicleInfo}\nLÁNSUPPLÝSINGAR:\n- Lánsfjárhæð: ${formatIcelandicNumber(amt)} kr.\n- Lántökugjald: ${formatIcelandicNumber(lant)} kr.\n- Heildarfjárhæð: ${formatIcelandicNumber(total)} kr.\n- Vextir: ${rate}%\n- Lánstími: ${months} mán.\n- Greiðsluseðlagjald: ${formatIcelandicNumber(grs)} kr.\n- Mánaðarleg greiðsla: ${formatIcelandicNumber(Math.round(mp + grs))} kr.\n\nFONDO ehf. | Kt. 521216-1310`;
    const w = window.open('', '_blank');
    w.document.write(`<pre style="font-family:monospace;font-size:14px;padding:20px;">${content}</pre>`);
    w.document.close(); w.print();
  };

  const handleSendEmail = () => {
    if (amt <= 0 || months <= 0) { toast.error("Vinsamlegast fylltu út lánsupplýsingar"); return; }
    const mp = calcMonthly();
    setEmailPreviewData({
      to_email: d.email || '', recipient_name: d.name || 'Viðskiptavinur',
      loan_type: cfg.emailLabel, loan_amount: amt, lantokugjald: lant,
      total_loan: amt + lant, interest_rate: rate, months,
      monthly_payment: Math.round(mp + grs), greidslus: grs,
    });
    onOpenChange(false);
    setShowEmailPreview(true);
  };

  const handleSave = async () => {
    if (!d.kennitala || !d.name || !d.address || !d.phone || !d.email || !d.amount || !d.months || !d.vextir) {
      toast.error("Vinsamlegast fylltu út alla reiti"); return;
    }
    if (cfg.validateVehicle && !d.vehicleType && !d.registrationNumber) {
      toast.error("Vinsamlegast fylltu út upplýsingar um ökutæki"); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email)) {
      toast.error("Vinsamlegast sláðu inn gilt netfang"); return;
    }
    if (ahk > maxAhk) {
      toast.warning(`ÁHK (${(ahk*100).toFixed(2)}%) er yfir löglegu hámarki ${(maxAhk*100).toFixed(2)}%.`, { duration: 5000 });
    }

    try {
      const loanAmount = parseFloat(stripDots(String(d.amount)));
      const appData = {
        borrower_info: {
          full_name: d.name, email: d.email, phone: `+354${d.phone}`,
          ssn: d.kennitala, date_of_birth: "1990-01-01", address: d.address,
          city: d.city || "Reykjavík", state: "IS", zip_code: d.postalCode || "101",
          employment_status: "employed", monthly_income: 500000, monthly_expenses: 300000,
        },
        loan_amount: loanAmount,
        loan_type: cfg.loanType,
        loan_purpose: cfg.loanPurpose,
        requested_term_months: parseInt(d.months),
        interest_rate: parseFloat(d.vextir),
        lantokugjald: parseFloat(stripDots(String(d.lantokugjald))) || 0,
        greidslus: parseFloat(stripDots(String(d.greidslus))),
        first_payment_date: d.firstPaymentDate,
        vehicle_type: d.vehicleType || "",
        registration_number: d.registrationNumber || "",
        first_registration_date: d.firstRegistrationDate || "",
        formData: {
          vehicleType: d.vehicleType || "",
          registrationNumber: d.registrationNumber || "",
          firstRegistrationDate: d.firstRegistrationDate || "",
        },
        payment_schedule: generatePaymentSchedule(
          loanAmount + (parseFloat(stripDots(String(d.lantokugjald))) || 0),
          parseInt(d.months), parseFloat(d.vextir),
          parseFloat(stripDots(String(d.greidslus))),
          d.firstPaymentDate ? new Date(d.firstPaymentDate) : null, new Date()
        ),
        radstofun: d.radstofun ? {
          kennitala: d.radstofunKennitala, name: d.radstofunName,
          banki: d.radstofunBanki, hofudbok: d.radstofunHofudbokl, reikningur: d.radstofunReikningur,
        } : null,
      };
      if (cfg.hasPurchasePrice) {
        appData.purchase_price = parseFloat(stripDots(String(d.purchasePrice))) || 0;
        appData.deposit = parseFloat(stripDots(String(d.deposit))) || 0;
      }

      if (editMode && editingAppId) {
        await axios.put(`${API}/loans/${editingAppId}`, appData);
        toast.success(cfg.editMsg);
        setEditMode(false); setEditingAppId(null);
      } else {
        if (skuldbreytingOldLoanId) {
          appData.isSkuldbreyting = true;
          appData.oldLoanId = skuldbreytingOldLoanId;
          appData.status = 'pending';
        }
        const response = await axios.post(`${API}/loans`, appData);
        saveKennitalCache(d.kennitala, d.name, d.address, d.postalCode, d.city, d.phone, d.email);
        if (skuldbreytingOldLoanId) {
          toast.success(cfg.skuldbreytingMsg);
          setSkuldbreytingOldLoanId(null);
        } else {
          toast.success(cfg.successMsg);
        }
        if (cfg.showPdfAfterSave && setPdfApp && setPdfType) {
          setPdfApp({ ...response.data, formData: d });
          setPdfType(cfg.pdfType);
        }
      }
      onOpenChange(false);
      setD(getDefaultForm());
      fetchApplications();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error(error.response?.data?.detail || `Villa kom upp við að skrá ${cfg.loanPurpose.toLowerCase()}`);
    }
  };

  const isOver = ahk > 0 && ahk > maxAhk;
  const isUnder = ahk > 0 && ahk <= maxAhk;
  const displayAhk = ahk > 0 ? (ahk * 100).toFixed(1) : (maxAhk * 100).toFixed(1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-0">
          <DialogTitle className="text-sm font-semibold flex items-center justify-between">
            <span>{cfg.title}</span>
            <div className="flex flex-col items-center mr-6">
              <span className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-[9px] font-bold ${isOver ? 'border-red-500 text-red-600' : isUnder ? 'border-green-500 text-green-600' : 'border-gray-300 text-gray-600'}`}
                title={ahk > 0 ? `ÁHK: ${(ahk*100).toFixed(2)}% | Hámark: ${(maxAhk*100).toFixed(2)}%` : `Hámarks ÁHK: ${(maxAhk*100).toFixed(2)}%`}>
                {displayAhk}%
              </span>
              <button type="button" onClick={() => setShowMiniSpreadsheet(!showMiniSpreadsheet)}
                className="mt-0.5 text-green-500 hover:text-green-700 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points={showMiniSpreadsheet ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
                </svg>
              </button>
            </div>
          </DialogTitle>
          <MiniSpreadsheet show={showMiniSpreadsheet} />
        </DialogHeader>

        <div className="space-y-1 py-0">
          {/* Vehicle Section (Ved/Kaupleiga only) */}
          {cfg.hasVehicleLookup && (
            <>
              <div className="border-t pt-0.5 mt-0.5">
                <Label className="text-[9px] font-semibold mb-0">Ökutækjaupplýsingar</Label>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <Label className="text-[9px] mb-0">Fastanúmer</Label>
                  <Input className="h-6 text-xs" value={d.registrationNumber}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
                      setD({ ...d, registrationNumber: v });
                      if (v.length >= 4 && !vehicleLookupLoading) doVehicleLookup(v);
                    }}
                    onBlur={() => { if (d.registrationNumber?.length >= 3 && !vehicleLookupLoading) doVehicleLookup(d.registrationNumber); }}
                    placeholder="ABC12" maxLength={6} disabled={vehicleLookupLoading} />
                </div>
                <div>
                  <Label className="text-[9px] mb-0">Fyrsta skráning</Label>
                  <Input className="h-6 text-xs"
                    type="date" value={d.firstRegistrationDate}
                    onChange={(e) => setD({ ...d, firstRegistrationDate: e.target.value })}
                    disabled={vehicleLookupLoading} />
                </div>
              </div>
              <div>
                <Label className="text-[9px] mb-0 flex items-center gap-1">Tegund
                  {vehicleLookupLoading && <Loader2 className="h-2 w-2 animate-spin text-blue-600" />}
                  {vehicleLookupFailed && <span className="text-[8px] text-orange-500 font-normal">— handvirkt</span>}
                </Label>
                <Input className={`h-6 text-xs ${vehicleLookupFailed ? 'border-orange-300' : ''}`}
                  value={d.vehicleType} onChange={(e) => setD({ ...d, vehicleType: e.target.value })}
                  placeholder="T.d. Toyota Corolla" disabled={vehicleLookupLoading} />
              </div>
              <div className="border-t pt-0.5 mt-0.5">
                <Label className="text-[9px] font-semibold mb-0">Upplýsingar um lántaka</Label>
              </div>
            </>
          )}

          {/* Simple vehicle fields for Skuldabréf only */}
          {!cfg.hasVehicleLookup && (
            <div className="grid grid-cols-2 gap-1">
              <div>
                <Label className="text-[9px] mb-0 flex items-center gap-1">Fastanúmer (tengsl)
                  {vehicleLookupLoading && <Loader2 className="h-2 w-2 animate-spin text-blue-600" />}
                </Label>
                <Input className="h-6 text-xs" value={d.registrationNumber}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
                    setD({ ...d, registrationNumber: v });
                    if (v.length >= 4 && !vehicleLookupLoading) doVehicleLookup(v);
                  }}
                  onBlur={() => { if (d.registrationNumber?.length >= 3 && !vehicleLookupLoading) doVehicleLookup(d.registrationNumber); }}
                  placeholder="T.d. AB123" maxLength={6} disabled={vehicleLookupLoading} />
              </div>
              <div>
                <Label className="text-[9px] mb-0 flex items-center gap-1">Tegund (tengsl)
                  {vehicleLookupFailed && <span className="text-[8px] text-orange-500 font-normal">— handvirkt</span>}
                </Label>
                <Input className="h-6 text-xs" value={d.vehicleType}
                  onChange={(e) => setD({ ...d, vehicleType: e.target.value })} placeholder="T.d. Toyota Yaris" />
              </div>
            </div>
          )}

          {/* Person Info */}
          <div className="grid grid-cols-2 gap-1">
            <div>
              <Label className="text-[9px] mb-0">Kennitala</Label>
              <Input className="h-6 text-xs" value={d.kennitala}
                onChange={(e) => setD({ ...d, kennitala: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                onBlur={(e) => { if (e.target.value.length === 10 && !lookupLoading) lookupPerson(e.target.value, loanType); }}
                placeholder="0000000000" maxLength={10} disabled={lookupLoading} />
            </div>
            <div>
              <Label className="text-[9px] mb-0">Nafn</Label>
              {lookupLoading ? (
                <div className="h-7 px-1.5 py-0 bg-gray-50 border border-gray-200 rounded text-xs flex items-center">
                  <Loader2 className="h-2.5 w-2.5 animate-spin text-blue-600 mr-1" /><span className="text-blue-600">Hleð...</span>
                </div>
              ) : (
                <Input className="h-6 text-xs" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Fullt nafn" />
              )}
            </div>
          </div>

          <div>
            <Label className="text-[9px] mb-0">Heimilisfang</Label>
            {lookupLoading ? (
              <div className="h-7 px-1.5 py-0 bg-gray-50 border border-gray-200 rounded text-xs flex items-center">
                <Loader2 className="h-2.5 w-2.5 animate-spin text-blue-600 mr-1" /><span className="text-blue-600">Hleð...</span>
              </div>
            ) : (
              <Input className="h-6 text-xs" value={d.address} onChange={(e) => setD({ ...d, address: e.target.value })} placeholder="Heimilisfang" />
            )}
          </div>

          <div className="grid grid-cols-2 gap-1">
            <div>
              <Label className="text-[9px] mb-0">Póstnúmer</Label>
              <Input className="h-6 text-xs" value={d.postalCode}
                onChange={(e) => setD({ ...d, postalCode: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                placeholder="000" maxLength={3} />
            </div>
            <div>
              <Label className="text-[9px] mb-0">Staður/Borg</Label>
              <Input className="h-6 text-xs" value={d.city} onChange={(e) => setD({ ...d, city: e.target.value })} placeholder="Reykjavík" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-1">
            <div>
              <Label className="text-[9px] mb-0">Símanúmer</Label>
              <div className="flex items-center gap-1">
                <span className="text-[9px] text-gray-600 px-1 py-0 bg-gray-100 rounded border h-6 flex items-center">+354</span>
                <Input className="h-6 text-xs flex-1" value={d.phone}
                  onChange={(e) => setD({ ...d, phone: e.target.value.replace(/\D/g, '').slice(0, 7) })}
                  placeholder="0000000" maxLength={7} />
              </div>
            </div>
            <div>
              <Label className="text-[9px] mb-0">Netfang</Label>
              <Input className="h-6 text-xs" type="email" value={d.email}
                onChange={(e) => setD({ ...d, email: e.target.value })} placeholder="netfang@example.is" />
            </div>
          </div>

          {/* Loan Amount Section */}
          {cfg.hasPurchasePrice ? (
            <>
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <Label className="text-[9px] mb-0">Kaupverð</Label>
                  <Input className="h-6 text-xs" type="text" value={fmtNum(d.purchasePrice || "")}
                    onChange={(e) => {
                      const raw = stripDots(e.target.value);
                      const dep = parseFloat(stripDots(d.deposit)) || 0;
                      const la = parseFloat(raw) - dep;
                      const fee = la > 0 ? Math.max(29900, Math.min(89900, Math.round(la * 0.089))) : "";
                      setD({ ...d, purchasePrice: raw, amount: la > 0 ? la.toString() : "", lantokugjald: fee });
                    }} placeholder="1.500.000" />
                </div>
                <div>
                  <Label className="text-[9px] mb-0">Innborgun</Label>
                  <Input className="h-6 text-xs" type="text" value={fmtNum(d.deposit || "")}
                    onChange={(e) => {
                      const raw = stripDots(e.target.value);
                      const pp = parseFloat(stripDots(d.purchasePrice)) || 0;
                      const la = pp - parseFloat(raw);
                      const fee = la > 0 ? Math.max(29900, Math.min(89900, Math.round(la * 0.089))) : "";
                      setD({ ...d, deposit: raw, amount: la > 0 ? la.toString() : "", lantokugjald: fee });
                    }} placeholder="500.000" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <Label className="text-[9px] mb-0">Lánsfjárhæð (Kaupverð - Innborgun)</Label>
                  <Input className="h-6 text-xs bg-gray-100" type="text"
                    value={d.amount ? formatIcelandicNumber(parseFloat(d.amount)) : ""} disabled placeholder="1.000.000" />
                </div>
                <div>
                  <Label className="text-[9px] mb-0">Lántökugjald</Label>
                  <Input className="h-6 text-xs" type="text" value={fmtNum(d.lantokugjald || "")}
                    onChange={(e) => setD({ ...d, lantokugjald: stripDots(e.target.value) })} placeholder="29.900" />
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-1">
              <div>
                <Label className="text-[9px] mb-0">Lánsfjárhæð</Label>
                <Input className="h-6 text-xs" type="text" value={fmtNum(d.amount || "")}
                  onChange={(e) => {
                    const raw = stripDots(e.target.value);
                    const numAmt = parseFloat(raw);
                    const fee = numAmt ? Math.max(29900, Math.min(89900, Math.round(numAmt * 0.089))) : "";
                    setD({ ...d, amount: raw, lantokugjald: fee });
                  }} placeholder="1.000.000" />
              </div>
              <div>
                <Label className="text-[9px] mb-0">Lántökugjald</Label>
                <Input className="h-6 text-xs" type="text" value={fmtNum(d.lantokugjald || "")}
                  onChange={(e) => setD({ ...d, lantokugjald: stripDots(e.target.value) })} placeholder="29.900" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-1">
            <div>
              <Label className="text-[9px] mb-0">Greiðsluseðill</Label>
              <Input className="h-6 text-xs" type="text" value={fmtNum(d.greidslus || "")}
                onChange={(e) => setD({ ...d, greidslus: stripDots(e.target.value) })} placeholder="250" />
            </div>
            <div>
              <Label className="text-[9px] mb-0">Lánstími (mánuðir)</Label>
              <Input className="h-6 text-xs" type="number" value={d.months}
                onChange={(e) => setD({ ...d, months: e.target.value })} placeholder="36" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-1">
            <div>
              <Label className="text-[9px] mb-0">Vextir</Label>
              <div className="flex items-center gap-1">
                <Input className="h-6 text-xs flex-1" type="number" step="0.01" value={d.vextir}
                  onChange={(e) => setD({ ...d, vextir: e.target.value })} placeholder="11.20" />
                <span className="text-[9px] text-gray-600 px-1 py-0 bg-gray-100 rounded border h-6 flex items-center">%</span>
              </div>
            </div>
            <div>
              <Label className="text-[9px] mb-0">Fyrsti gjalddagi</Label>
              <Input className="h-6 text-xs" type="date" value={d.firstPaymentDate}
                onChange={(e) => setD({ ...d, firstPaymentDate: e.target.value })} />
            </div>
          </div>

          {/* Monthly Payment */}
          {d.amount && d.months && d.vextir && (
            <div className={`${accentBg} border rounded p-0.5`}>
              <div className="flex justify-between items-center">
                <span className="text-[9px] font-semibold text-gray-700">Mánaðarleg greiðsla:</span>
                <span className={`text-sm font-bold ${accentText}`}>
                  {formatIcelandicNumber(Math.round(calcMonthly()))} kr.
                </span>
              </div>
            </div>
          )}

          {/* Raðstofun */}
          <div className="border-t pt-1 mt-1">
            <div className="flex items-center space-x-2">
              <input type="checkbox" id={`radstofun-${loanType}`} checked={d.radstofun}
                onChange={(e) => setD({ ...d, radstofun: e.target.checked })} className="h-3 w-3" />
              <Label htmlFor={`radstofun-${loanType}`} className="text-[10px] font-semibold mb-0">Raðstofun</Label>
            </div>
            {d.radstofun && (
              <div className="space-y-1 mt-1">
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label className="text-[9px] mb-0">Kennitala</Label>
                    <div className="flex items-center gap-1">
                      <Input className="h-6 text-xs flex-1" value={d.radstofunKennitala}
                        onChange={(e) => setD({ ...d, radstofunKennitala: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        onBlur={async () => {
                          if (d.radstofunKennitala.length === 10) {
                            try {
                              const res = await axios.get(`${API}/lookup-person`, { params: { kennitala: d.radstofunKennitala } });
                              if (res.data.success && res.data.person) {
                                setD(prev => ({ ...prev, radstofunName: res.data.person.name || "" }));
                                toast.success("Nafn fundið");
                              }
                            } catch { toast.error("Villa við að fletta upp kennitölu"); }
                          }
                        }}
                        placeholder="0000000000" />
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[9px] mb-0">Tilgreina banka</Label>
                    <Input className="h-6 text-xs" value={d.radstofunName}
                      onChange={(e) => setD({ ...d, radstofunName: e.target.value })} placeholder="Tilgreina banka" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  <div>
                    <Label className="text-[9px] mb-0">Banki</Label>
                    <Input className="h-6 text-xs" value={d.radstofunBanki}
                      onChange={(e) => setD({ ...d, radstofunBanki: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="0000" />
                  </div>
                  <div>
                    <Label className="text-[9px] mb-0">Höfuðbók</Label>
                    <Input className="h-6 text-xs" value={d.radstofunHofudbokl}
                      onChange={(e) => setD({ ...d, radstofunHofudbokl: e.target.value.replace(/\D/g, '').slice(0, 2) })} placeholder="00" />
                  </div>
                  <div>
                    <Label className="text-[9px] mb-0">Reikningur</Label>
                    <Input className="h-6 text-xs" value={d.radstofunReikningur}
                      onChange={(e) => setD({ ...d, radstofunReikningur: e.target.value.replace(/\D/g, '').slice(0, 6) })} placeholder="000000" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Skuldbreyting Note */}
          {skuldbreytingOldLoanId && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
              <p className="text-xs text-yellow-800">
                <strong>Raðstafa inn á:</strong> {
                  skuldbreytingOldLoanIds?.length > 0
                    ? skuldbreytingOldLoanIds.map(id => loanNumberMap[id] ? fmtLoanNum(loanNumberMap[id]) : id).join(', ')
                    : (loanNumberMap[skuldbreytingOldLoanId] ? fmtLoanNum(loanNumberMap[skuldbreytingOldLoanId]) : skuldbreytingOldLoanId)
                }
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-1 pt-0">
            <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={resetAndClose}>Hætta við</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs px-2 border-gray-400 text-gray-600 hover:bg-gray-50" onClick={handlePrint}>
              <Printer className="h-3 w-3 mr-1" />Prenta
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs px-2 border-green-500 text-green-600 hover:bg-green-50" onClick={handleSendEmail}>
              <Send className="h-3 w-3 mr-1" />Senda
            </Button>
            <Button size="sm" className={`${btnClass} h-7 text-xs px-2`} onClick={handleSave}>Skrá</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Backward-compatible exports
export const SkuldabrefFormDialog = (props) => <LoanFormDialog loanType="skuldabref" {...props} />;
export const VedskuldabrefFormDialog = (props) => <LoanFormDialog loanType="vedskuldabref" {...props} />;
export const KaupleiguFormDialog = (props) => <LoanFormDialog loanType="kaupleigusamning" {...props} />;
